/**
 * @file GeneratePlan.tsx
 * @description Component for generating and editing editorial plans.
 * Allows users to create, modify, and save content plans using an editable grid.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { ColDef, RowNode } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";

import { ContentDetails } from "../../types";
import config from "../../config/config";
import { auth } from "../../config/firebaseConfig";
import log from "../../config/logging";
import useGenerateContent from "../../hooks/useGenerateContent";
import RelatedKeywordsEditor from "../../components/common/cellComponents/RelatedKeywordsEditor";
import RelatedKeywordsRenderer from "../../components/common/cellComponents/RelatedKeywordsRenderer";
import StatusRenderer from "../../components/common/cellComponents/StatusRenderer";
import { useClients } from "../../hooks/useClients";
import { useWebsites } from "../../hooks/useWebsites";
import { excludeLastEmptyRow } from "../../components/utilities/planUtils";
import Loading from "../../components/common/Loading";
import ImportSpreadsheetButton from "../../components/common/ImportSpreadsheetButton";
import ImportSpreadsheetModal from "../../components/common/ImportSpreadsheetModal";
import { mapSpreadsheetData } from "../../components/utilities/spreadsheetUtils";
import { validateUrl } from "../../utils/urlUtils";

const GeneratePlan: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, loading] = useAuthState(auth);
  const {
    generateContent,
    isLoading: isGenerating,
    setIsLoading: setIsGenerating,
  } = useGenerateContent();

  const [planData, setPlanData] = useState<ContentDetails[]>([]);
  const [selectedRows, setSelectedRows] = useState<RowNode[]>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [validatedRows, setValidatedRows] = useState<Set<number>>(new Set());
  
  // Plan-level preset state
  const [planTitle, setPlanTitle] = useState<string>("Nuovo piano editoriale");
  const [selectedPlanClientId, setSelectedPlanClientId] = useState<string>("");
  const [selectedPlanWebsiteId, setSelectedPlanWebsiteId] = useState<string>("");
  
  // Hooks for presets - only load when user is authenticated
  const { clients, isLoading: isLoadingClients } = useClients();
  const { websites } = useWebsites(
    user && selectedPlanClientId ? selectedPlanClientId : undefined
  );

  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
      editable: true,
      flex: 2,
    }),
    []
  );

  // Plan-level manual fields (when no preset is selected)
  const [planWebsite, setPlanWebsite] = useState<string>("");
  const [planContentType, setPlanContentType] = useState<string>("");
  const [planClientInfo, setPlanClientInfo] = useState<string>("");
  
  // Track if manual fields have been saved to avoid overwriting with null
  const [savedManualFields, setSavedManualFields] = useState<{ website?: string; contentType?: string; clientInfo?: string; name?: string }>({});
  // Use a ref to track saved name for immediate access (avoiding async state updates)
  const savedNameRef = useRef<string | undefined>(undefined);

  const [colDefs] = useState<ColDef[]>(() => {
    const columns: ColDef[] = [
      {
        field: "title",
        headerName: "Titolo",
        flex: 3,
        editable: (params) => params.data.status !== "completed",
      },
      {
        field: "keyword",
        headerName: "Keyword principale *",
        editable: (params) => params.data.status !== "completed",
        cellStyle: (params) => {
          const rowIndex = params.node?.rowIndex ?? -1;
          if (
            validatedRows.has(rowIndex) &&
            (!params.value || !params.value.trim())
          ) {
            return { backgroundColor: "#fef3c7" }; // Light yellow for missing required field
          }
          return null;
        },
      },
      {
        field: "relatedKeywords",
        headerName: "Keyword correlate",
        flex: 4,
        cellEditor: RelatedKeywordsEditor,
        cellRenderer: RelatedKeywordsRenderer,
        valueFormatter: (params) => params.value.join(", "),
        valueParser: (params) => {
          const trimmedValue = params.newValue.trim();
          if (trimmedValue.includes(",")) {
            return trimmedValue.split(",").map((kw) => kw.trim()).filter(Boolean);
          } else if (trimmedValue.includes("\n") || trimmedValue.includes("\r\n")) {
            return trimmedValue.split(/\r?\n/).map((kw) => kw.trim()).filter(Boolean);
          } else if (trimmedValue) {
            return [trimmedValue];
          }
          return [];
        },
        editable: (params) => params.data.status !== "completed",
      },
      {
        field: "peopleAlsoAsk",
        headerName: "People Also Ask",
        flex: 4,
        cellEditor: RelatedKeywordsEditor,
        cellRenderer: RelatedKeywordsRenderer,
        valueFormatter: (params) => params.value?.join(", ") || "",
        valueParser: (params) => {
          const trimmedValue = params.newValue.trim();
          if (trimmedValue.includes(",")) {
            return trimmedValue.split(",").map((q) => q.trim()).filter(Boolean);
          } else if (trimmedValue.includes("\n") || trimmedValue.includes("\r\n")) {
            return trimmedValue.split(/\r?\n/).map((q) => q.trim()).filter(Boolean);
          } else if (trimmedValue) {
            return [trimmedValue];
          }
          return [];
        },
        editable: (params) => params.data.status !== "completed",
      },
      {
        field: "status",
        headerName: "Stato",
        flex: 1,
        editable: false,
        cellRenderer: StatusRenderer,
      },
    ];

    /* Temporary column for development
    if (process.env.REACT_APP_ENV === "development") {
      columns.unshift({
        field: "contentDetailId",
        editable: false,
      });
    }
    */

    return columns;
  });

  const addEmptyRow = (data: ContentDetails[]) => [
    ...data,
    {
      website: "", // Required by type, but not used (plan-level instead)
      contentType: "",
      clientInfo: "",
      keyword: "",
      relatedKeywords: [],
      peopleAlsoAsk: [],
      title: "",
      contentDetailId: "",
      status: "pending",
    },
  ];

  const handleAddRow = () => {
    setPlanData((prevData) => addEmptyRow(prevData));
  };

  const handleGeneratePlan = async () => {
    if (!planData || !Array.isArray(planData)) {
      log.error("planData is not an array or is null");
      return;
    }

    // Validate required fields before generating
    const filteredData = excludeLastEmptyRow(planData);
    const rowsWithMissingKeyword: number[] = [];
    const rowsWithInvalidUrl: number[] = [];
    const invalidRowIndices = new Set<number>();

    // Check if plan-level website is set (either from preset or manual)
    const planWebsiteValue = selectedPlanWebsiteId 
      ? websites.find((w) => w.id === selectedPlanWebsiteId)?.website 
      : planWebsite;

    filteredData.forEach((data, index) => {
      if (!data.keyword || !data.keyword.trim()) {
        rowsWithMissingKeyword.push(index + 1);
        invalidRowIndices.add(index);
      }
      // Validate website if present (either in data or from plan-level)
      const websiteToValidate = data.website || planWebsiteValue;
      if (websiteToValidate && websiteToValidate.trim()) {
        const validation = validateUrl(websiteToValidate);
        if (!validation.isValid) {
          rowsWithInvalidUrl.push(index + 1);
          invalidRowIndices.add(index);
        }
      }
    });

    // Mark rows as validated to show styling
    setValidatedRows(invalidRowIndices);

    if (rowsWithMissingKeyword.length > 0) {
      toast.error(
        `Compila la Keyword principale per tutte le righe prima di lanciare la generazione. Righe mancanti: ${rowsWithMissingKeyword.join(", ")}`
      );
      return;
    }

    if (rowsWithInvalidUrl.length > 0) {
      toast.error(
        `L'URL indicato non risulta raggiungibile per le righe ${rowsWithInvalidUrl.join(", ")}. Controlla che sia corretto o copialo dalla barra del browser.`
      );
      return;
    }

    setIsGenerating(true);
    const startTime = performance.now();
    log.info("Starting content generation for plan", { planId });

    const pendingContentDetails = filteredData.filter(
      (data) => data.status === "pending" && data.contentDetailId
    );

    log.debug("Found pending content details", {
      count: pendingContentDetails.length,
      contentIds: pendingContentDetails.map((d) => d.contentDetailId),
    });

    for (let i = 0; i < pendingContentDetails.length; i++) {
      const data = pendingContentDetails[i];
      const contentStartTime = performance.now();

      log.debug(`Generating content ${i + 1}/${pendingContentDetails.length}`, {
        contentDetailId: data.contentDetailId,
      });

      // Set the `status` column to "generating"
      setPlanData((prevData) =>
        prevData.map((item) =>
          item.contentDetailId === data.contentDetailId
            ? { ...item, status: "generating" }
            : item
        )
      );

      // Apply plan-level values (always use plan-level, not row-level)
      const planWebsiteValue = selectedPlanWebsiteId 
        ? websites.find((w) => w.id === selectedPlanWebsiteId)?.website || ""
        : planWebsite;
      const planContentTypeValue = selectedPlanWebsiteId 
        ? websites.find((w) => w.id === selectedPlanWebsiteId)?.contentType || ""
        : planContentType;
      const planClientInfoValue = selectedPlanClientId 
        ? clients.find((c) => c.id === selectedPlanClientId)?.name || ""
        : planClientInfo;

      const enrichedData = {
        ...data,
        website: planWebsiteValue,
        contentType: planContentTypeValue,
        clientInfo: planClientInfoValue,
        contentDetailId: data.contentDetailId,
      };

      try {
        await generateContent(
          enrichedData,
          () => {
            // Set the `status` column to "completed"
            setPlanData((prevData) =>
              prevData.map((item) =>
                item.contentDetailId === data.contentDetailId
                  ? { ...item, status: "completed" }
                  : item
              )
            );

            const contentDuration =
              (performance.now() - contentStartTime) / 1000;
            log.info("Content generation completed", {
              contentDetailId: data.contentDetailId,
              durationSeconds: contentDuration.toFixed(2),
            });
          }
        );
      } catch (error) {
        log.error("Error generating content", {
          error,
          contentDetailId: data.contentDetailId,
        });
        toast.error(
          `Errore durante la generazione del contenuto per ${data.keyword}`
        );
        // Set the status back to "pending" if generation fails
        setPlanData((prevData) =>
          prevData.map((item) =>
            item.contentDetailId === data.contentDetailId
              ? { ...item, status: "pending" }
              : item
          )
        );
      }
    }

    const totalDuration = (performance.now() - startTime) / 1000;
    log.info("Plan generation completed", {
      planId,
      totalContentCount: pendingContentDetails.length,
      totalDurationSeconds: totalDuration.toFixed(2),
    });

    setIsGenerating(false);
    // Redirect to Plan page after all content is generated
    navigate(`/piano/${planId}`);
  };

  const handleRowValueChanged = (params: any) => {
    if (params.data && params.node && params.node.rowIndex !== null) {
      const updatedData = [...planData];
      const rowIndex = params.node.rowIndex;
      let updatedRow = { ...params.data };

      // Note: website, contentType, clientInfo are no longer editable in grid
      // They are managed at plan level

      updatedData[rowIndex] = updatedRow;

      console.log("Updated Data:", updatedData);

      setPlanData(updatedData);

      if (rowIndex === planData.length - 1) {
        const newData = addEmptyRow(updatedData);
        console.log("New Data with Empty Row:", newData);
        setPlanData(newData);
      }

      // Save plan on row value change
      if (!planId) {
        savePlan(updatedData);
      } else {
        if (updatedRow.contentDetailId) {
          updateContentDetail(updatedRow);
        } else {
          createContentDetail(updatedRow);
        }
      }
    }
  };

  const handleSelectionChanged = (params: any) => {
    setSelectedRows(params.api.getSelectedNodes());
  };

  const handleDeleteSelected = async () => {
    const selectedIds = selectedRows.map((row) => row.id);
    const updatedData = planData.filter(
      (_, index) => !selectedIds.includes(index.toString())
    );

    // Delete content details from backend
    for (const row of selectedRows) {
      if (row.data.contentDetailId) {
        await deleteContentDetail(row.data.contentDetailId);
      }
    }

    setPlanData(updatedData);
    setSelectedRows([]);
  };

  /**
   * Helper function to get field value with priority:
   * manualFields > savedRef (if provided) > savedState > currentState > defaultValue
   */
  const getFieldValue = useCallback((
    manualValue: string | undefined,
    savedRef: string | undefined,
    savedState: string | undefined,
    currentState: string,
    defaultValue: string
  ): string => {
    if (manualValue !== undefined) return manualValue;
    if (savedRef !== undefined) return savedRef;
    if (savedState !== undefined) return savedState;
    return currentState || defaultValue;
  }, []);

  const savePlan = useCallback(
    async (data: ContentDetails[], manualFields?: { website?: string; contentType?: string; clientInfo?: string; name?: string; clientId?: string; websiteId?: string }) => {
      const isUpdating = !!planId;
      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error("User not authenticated");
        }

        const token = await user.getIdToken(true);
        if (!token) {
          throw new Error("Token not found");
        }

        const filteredData = excludeLastEmptyRow(data);

        // Keep contentDetailId in payload for updates (backend needs it to update existing documents)
        // Only remove it if it's a new plan (no planId) or if contentDetailId is undefined/null
        const payloadData = filteredData.map((item) => {
          const { contentDetailId, ...rest } = item;
          // Include contentDetailId only if it exists and we're updating an existing plan
          if (planId && contentDetailId) {
            return { ...rest, contentDetailId };
          }
          return rest;
        });

        log.debug(isUpdating ? "Updating plan..." : "Saving new plan...");

        const response = await fetch(
          planId
            ? `${config.backendUrl}/api/plans/${planId}`
            : `${config.backendUrl}/api/plans`,
          {
            method: planId ? "PUT" : "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: (() => {
              // Get field values with proper priority using helper function
              const websiteValue = getFieldValue(
                manualFields?.website,
                undefined, // No ref for website
                savedManualFields.website,
                planWebsite,
                ""
              );
              const contentTypeValue = getFieldValue(
                manualFields?.contentType,
                undefined, // No ref for contentType
                savedManualFields.contentType,
                planContentType,
                ""
              );
              const clientInfoValue = getFieldValue(
                manualFields?.clientInfo,
                undefined, // No ref for clientInfo
                savedManualFields.clientInfo,
                planClientInfo,
                ""
              );
              
              // Name uses ref for immediate access (always included in payload)
              const nameValue = getFieldValue(
                manualFields?.name,
                savedNameRef.current,
                savedManualFields.name,
                planTitle,
                "Nuovo piano editoriale"
              );
              
              // Update ref immediately for next savePlan call
              if (nameValue && nameValue !== "Nuovo piano editoriale") {
                savedNameRef.current = nameValue;
              }
              
              // Update saved fields: merge manualFields (if provided) with current state values
              const fieldsToSave: Partial<typeof savedManualFields> = {};
              if (manualFields) {
                // When manualFields provided, save only those fields that are explicitly set
                if (manualFields.website !== undefined) fieldsToSave.website = manualFields.website;
                if (manualFields.contentType !== undefined) fieldsToSave.contentType = manualFields.contentType;
                if (manualFields.clientInfo !== undefined) fieldsToSave.clientInfo = manualFields.clientInfo;
                if (manualFields.name !== undefined) fieldsToSave.name = manualFields.name;
              } else {
                // When no manualFields, save current state values if they exist
                if (planWebsite) fieldsToSave.website = planWebsite;
                if (planContentType) fieldsToSave.contentType = planContentType;
                if (planClientInfo) fieldsToSave.clientInfo = planClientInfo;
                if (nameValue && nameValue !== "Nuovo piano editoriale") fieldsToSave.name = nameValue;
              }
              
              if (Object.keys(fieldsToSave).length > 0) {
                setSavedManualFields(prev => ({ ...prev, ...fieldsToSave }));
              }
              
              // Build payload - only include fields that are being explicitly updated
              // If manualFields is provided, include those fields; otherwise only include if state has values
              const payload: any = {
                name: nameValue,
                description: "",
              };
              
              // Only send website if:
              // 1. It's in manualFields (explicitly being updated), OR
              // 2. We have a value in state or savedManualFields (to preserve existing value)
              // AND no websiteId preset is selected
              if ((manualFields?.website !== undefined || (websiteValue && websiteValue.trim())) && !selectedPlanWebsiteId) {
                payload.website = websiteValue || "";
              }
              
              // Only send contentType if:
              // 1. It's in manualFields (explicitly being updated), OR
              // 2. We have a value in state or savedManualFields (to preserve existing value)
              if (manualFields?.contentType !== undefined || (contentTypeValue && contentTypeValue.trim())) {
                payload.contentType = selectedPlanWebsiteId ? null : (contentTypeValue && contentTypeValue.trim() ? contentTypeValue : null);
              }
              
              // Only send clientInfo if:
              // 1. It's in manualFields (explicitly being updated), OR
              // 2. We have a value in state or savedManualFields (to preserve existing value)
              // AND no clientId preset is selected
              if ((manualFields?.clientInfo !== undefined || (clientInfoValue && clientInfoValue.trim())) && !selectedPlanClientId) {
                payload.clientInfo = selectedPlanWebsiteId ? null : (clientInfoValue && clientInfoValue.trim() ? clientInfoValue : null);
              }
              
              // Always include these fields - use manualFields if provided, otherwise use state
              payload.clientId = manualFields?.clientId !== undefined ? manualFields.clientId : (selectedPlanClientId || undefined);
              payload.websiteId = manualFields?.websiteId !== undefined ? manualFields.websiteId : (selectedPlanWebsiteId || undefined);
              
              payload.contentDetails = payloadData.map((detail) => {
                // If no preset is selected, use plan-level manual fields
                if (!selectedPlanWebsiteId) {
                  return {
                    ...detail,
                    website: detail.website || websiteValue || "",
                    contentType: detail.contentType || contentTypeValue || "",
                    clientInfo: detail.clientInfo || (selectedPlanClientId ? "" : (clientInfoValue || "")),
                  };
                }
                return detail;
              });
              return JSON.stringify(payload);
            })(),
          }
        );

        if (!response.ok) {
          throw new Error(`Errore: ${response.statusText}`);
        }

        const responseData = await response.json();
        log.debug("Response data from savePlan", { responseData });

        const newPlanId = responseData.planId || planId;
        if (newPlanId && newPlanId !== planId) {
          setPlanId(newPlanId);
          // Update URL using HashRouter navigation
          navigate(`/genera-piano/${newPlanId}`, { replace: true });
        }

        // Only update contentDetailIds if we have contentDetails
        if (responseData.contentDetailIds && responseData.contentDetailIds.length > 0) {
          if (responseData.contentDetailIds.length !== filteredData.length) {
            throw new Error("Mismatch between contentDetailIds and data length");
          }

          // Update contentDetailIds in planData
          const updatedPlanData = filteredData.map((item, index) => ({
            ...item,
            contentDetailId: responseData.contentDetailIds[index],
          }));

          // Add an empty row to the updated plan data
          const finalPlanData = addEmptyRow(updatedPlanData);
          setPlanData(finalPlanData);
        } else if (filteredData.length === 0) {
          // If no contentDetails, just ensure we have an empty row
          if (planData.length === 0) {
            setPlanData(addEmptyRow([]));
          }
        }

        log.debug(
          isUpdating ? "Plan updated successfully" : "Plan saved successfully",
          { responseData }
        );

        // Navigate to the plan page after successful save/update
        navigate(`/genera-piano/${newPlanId}`);
      } catch (error) {
        log.error(isUpdating ? "Error updating plan" : "Error saving plan", {
          error,
        });
        toast.error("Errore durante il salvataggio del piano editoriale");
      }
    },
    [
      planId,
      navigate,
      getFieldValue,
      savedManualFields,
      planWebsite,
      planContentType,
      planClientInfo,
      planTitle,
      selectedPlanClientId,
      selectedPlanWebsiteId,
      savedNameRef,
      planData.length,
    ]
  );

  const createContentDetail = async (contentDetail: ContentDetails) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken(true);
      if (!token) {
        throw new Error("Token not found");
      }

      // Remove contentDetailId from payload (frontend-only reference)
      const { contentDetailId, ...contentDetailPayload } = contentDetail;
      const payloadWithPlanId = { ...contentDetailPayload, planId };

      const response = await fetch(`${config.backendUrl}/api/contents/detail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payloadWithPlanId),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage =
          errorData?.message ||
          (errorData?.errorCode === "INVALID_URL"
            ? "L'URL indicato non risulta raggiungibile. Controlla che sia corretto o copialo dalla barra del browser."
            : errorData?.errorCode === "MISSING_REQUIRED_FIELD"
              ? "Compila la Keyword principale per tutte le righe prima di lanciare la generazione."
              : `Errore: ${response.statusText}`);
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      log.debug("Content detail created successfully", { responseData });

      // Update the contentDetailId and status in the planData
      setPlanData((prevData) =>
        prevData.map((item) =>
          item === contentDetail
            ? {
                ...item,
                contentDetailId: responseData.contentDetailId,
                status: "pending",
              }
            : item
        )
      );
    } catch (error) {
      log.error("Error creating content detail", { error });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Errore durante la creazione del dettaglio del contenuto";
      toast.error(errorMessage);
    }
  };

  const updateContentDetail = async (contentDetail: ContentDetails) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken(true);
      if (!token) {
        throw new Error("Token not found");
      }

      // Remove contentDetailId from payload (frontend-only reference)
      const { contentDetailId, ...contentDetailPayload } = contentDetail;

      const response = await fetch(
        `${config.backendUrl}/api/contents/${contentDetail.contentDetailId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(contentDetailPayload),
        }
      );

      if (!response.ok) {
        throw new Error(`Errore: ${response.statusText}`);
      }

      log.debug("Content detail updated successfully");
    } catch (error) {
      log.error("Error updating content detail", { error });
      toast.error(
        "Errore durante l'aggiornamento del dettaglio del contenuto."
      );
    }
  };

  const deleteContentDetail = async (contentDetailId: string) => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error("User not authenticated");
      }

      const token = await user.getIdToken(true);
      if (!token) {
        throw new Error("Token not found");
      }

      const response = await fetch(
        `${config.backendUrl}/api/contents/${contentDetailId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Errore: ${response.statusText}`);
      }

      log.debug("Content detail deleted successfully");
    } catch (error) {
      log.error("Error deleting content detail", { error });
      toast.error("Errore durante l'eliminazione del dettaglio del contenuto");
    }
  };

  const fetchPlanDetails = useCallback(async (planIdToFetch?: string) => {
    const targetPlanId = planIdToFetch || planId;
    if (!targetPlanId || !user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const token = await user.getIdToken(true);
      const response = await fetch(`${config.backendUrl}/api/plans/${targetPlanId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Errore: ${response.statusText}`);
      }

      const responseData = await response.json();
      log.debug("Raw response data:", responseData);

      if (!responseData || !responseData.success || !responseData.plan) {
        throw new Error("Dati del piano non validi nella risposta del server");
      }

      const { plan } = responseData;
      log.debug("Plan details fetched successfully", { plan });

      // Set plan-level state
      setPlanTitle(plan.name || "Nuovo piano editoriale");
      setSelectedPlanClientId(plan.clientId || "");
      setSelectedPlanWebsiteId(plan.websiteId || "");
      
      // Set manual fields from plan (not from contentDetails)
      if (!plan.websiteId) {
        const website = plan.website || "";
        const contentType = plan.contentType ?? "";
        const clientInfo = plan.clientInfo ?? "";
        setPlanWebsite(website);
        setPlanContentType(contentType);
        setPlanClientInfo(clientInfo);
        // Update saved fields from loaded plan data
        const loadedName = plan.name && plan.name !== "Nuovo piano editoriale" ? plan.name : undefined;
        if (loadedName) {
          savedNameRef.current = loadedName;
        }
        setSavedManualFields({
          website: website || undefined,
          contentType: contentType || undefined,
          clientInfo: clientInfo || undefined,
          name: loadedName,
        });
      } else {
        // Clear manual fields when preset is selected
        setPlanWebsite("");
        setPlanContentType("");
        setPlanClientInfo("");
        // Clear saved fields when preset is selected
        setSavedManualFields({});
      }

      const contentDetails = plan.contents || [];
      // Map the backend data structure to our frontend structure
      // Note: website, contentType, clientInfo are now plan-level fields
      // but we still need them in ContentDetails for type compatibility
      const formattedContentDetails = contentDetails.map((detail: any) => ({
        contentDetailId: detail.id,
        website: detail.website || "", // Will be overridden by plan-level values
        contentType: detail.contentType || detail.topic || "",
        clientInfo: detail.clientInfo || detail.audience || "",
        keyword: detail.keyword,
        relatedKeywords: detail.relatedKeywords || [],
        peopleAlsoAsk: detail.peopleAlsoAsk || [],
        title: detail.title || "",
        status: detail.status,
      }));
      // Always replace planData with fetched data to prevent duplicates
      // Only keep the empty row if there are no contentDetails
      if (formattedContentDetails.length > 0) {
        const newData = addEmptyRow(formattedContentDetails);
        setPlanData(newData);
      } else {
        // If no contentDetails, just add an empty row
        setPlanData(addEmptyRow([]));
      }
    } catch (error: unknown) {
      log.error("Error fetching plan details", { error });
      if (error instanceof Error) {
        toast.error(
          `Errore durante il recupero del piano editoriale: ${error.message}`
        );
      } else {
        toast.error(
          "Errore sconosciuto durante il recupero del piano editoriale."
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [planId, user]);

  const resetState = useCallback(() => {
    setPlanData([
      {
        website: "", // Required by ContentDetails type, but not used (plan-level instead)
        contentType: "",
        clientInfo: "",
        keyword: "",
        relatedKeywords: [],
        peopleAlsoAsk: [],
        title: "",
        contentDetailId: "",
        status: "pending",
      },
    ]);
    setSelectedRows([]);
    setPlanId(null);
    setIsLoading(false);
    setPlanTitle("Nuovo piano editoriale");
    setSelectedPlanClientId("");
    setSelectedPlanWebsiteId("");
    setPlanWebsite("");
    setPlanContentType("");
    setPlanClientInfo("");
    setSavedManualFields({});
    savedNameRef.current = undefined;
  }, []);

  useEffect(() => {
    const initializePlan = async () => {
      if (loading) return;

      if (!user) {
        log.warn("User not authenticated, redirecting to login");
        toast.error("Per favore, accedi per visualizzare questa pagina");
        navigate("/accedi");
        return;
      }

      // Wait a bit for authentication to fully settle
      await new Promise(resolve => setTimeout(resolve, 100));

      if (id) {
        // Always set planId if it's different from the URL param
        if (planId !== id) {
          setPlanId(id);
        }
        // Always fetch if we have an ID (even if planId was already set, to ensure data is loaded)
        setIsLoading(true);
        try {
          // Pass the ID directly to avoid timing issues with state updates
          await fetchPlanDetails(id);
        } catch (error) {
          log.error("Error in fetchPlanDetails", { error });
          toast.error(
            "Si è verificato un errore durante il caricamento dei dati del piano."
          );
          setIsLoading(false);
        }
      } else {
        // Reset state for new plan
        resetState();
      }
    };

    initializePlan();
  }, [user, loading, navigate, fetchPlanDetails, id, resetState, planId]);

  // Reset state when location changes
  useEffect(() => {
    if (!id) {
      resetState();
    }
  }, [location.pathname, id, resetState]);

  // When plan-level website preset is selected, populate website/contentType/clientInfo for empty rows
  useEffect(() => {
    if (selectedPlanWebsiteId && websites.length > 0) {
      const website = websites.find((w) => w.id === selectedPlanWebsiteId);
      if (website) {
        const client = clients.find((c) => c.id === website.clientId);
        setPlanData((prevData) =>
          prevData.map((row) => {
            // Only populate if fields are empty
            if (!row.website && !row.contentType && !row.clientInfo) {
              return {
                ...row,
                website: website.website,
                contentType: website.contentType,
                clientInfo: client?.name || "",
              };
            }
            return row;
          })
        );
      }
    } else if (selectedPlanClientId && !selectedPlanWebsiteId && clients.length > 0) {
      // If only client is selected, populate clientInfo for empty rows
      const client = clients.find((c) => c.id === selectedPlanClientId);
      if (client) {
        setPlanData((prevData) =>
          prevData.map((row) => {
            if (!row.clientInfo) {
              return {
                ...row,
                clientInfo: client.name,
              };
            }
            return row;
          })
        );
      }
    }
  }, [selectedPlanWebsiteId, selectedPlanClientId, websites, clients]);

  // Debug: log clients when they change
  useEffect(() => {
    if (clients.length > 0) {
      log.debug("Clients loaded in GeneratePlan", { count: clients.length, clients });
    }
  }, [clients]);

  const handleImportClick = () => {
    setIsImportModalOpen(true);
  };

  const handleImportClose = () => {
    setIsImportModalOpen(false);
  };

  const handleImportConfirm = async (mappedData: Partial<ContentDetails>[]) => {
    try {
      setIsImporting(true);
      const validatedData = mapSpreadsheetData(mappedData, colDefs);
      const newPlanData = [
        ...planData.slice(0, -1),
        ...validatedData,
        ...addEmptyRow([]),
      ];
      setPlanData(newPlanData);
      await savePlan(newPlanData);
      setIsImportModalOpen(false);
      toast.success("Dati importati con successo");
    } catch (error) {
      log.error("Error importing data", error);
      toast.error("Errore durante l'importazione dei dati");
    } finally {
      setIsImporting(false);
    }
  };

  if (loading || isLoading) {
    return <Loading />;
  }

  return (
    <section className="mx-auto max-w-content">
      <h2 className="!text-5xl !font-normal text-primary-content !mb-6 flex items-center gap-6">
        <ArticleOutlinedIcon className="text-5xl" />
        Genera piano editoriale
      </h2>
      <p className="!text-2xl text-primary-content font-light mb-16">
        Inserisci nella griglia i dettagli relativi ai contenuti da generare.
        Ogni riga corrisponde ad un contenuto.
      </p>
      <section className="flex flex-col gap-4 p-6 xl:p-12 bg-base-100 rounded-tl-6xl rounded-br-6xl">
        {/* Plan-level settings section */}
        <div className="mb-4">
          <h3 className="mb-2 text-primary !text-2xl !font-medium text-left">
            Dettagli del piano editoriale
          </h3>
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="plan-title" className="text-sm font-medium text-left">
                Titolo del piano editoriale
              </label>
              <input
                id="plan-title"
                type="text"
                value={planTitle}
                onChange={(e) => setPlanTitle(e.target.value)}
                onBlur={async (e) => {
                  // Always update state from input value
                  const currentTitle = e.target.value;
                  if (currentTitle !== planTitle) {
                    setPlanTitle(currentTitle);
                  }
                  // Trigger autosave with explicit name to ensure it's saved immediately
                  await savePlan(planData, { name: currentTitle });
                }}
                className="w-full rounded-2xl border border-gray-300 input"
                placeholder="Inserisci il titolo del piano editoriale"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="plan-preset-client" className="text-sm font-medium text-left">
                Cliente (opzionale)
              </label>
              <select
                id="plan-preset-client"
                value={selectedPlanClientId}
                onChange={async (e) => {
                  const newClientId = e.target.value;
                  setSelectedPlanClientId(newClientId);
                  setSelectedPlanWebsiteId(""); // Clear website when client changes
                  // Clear manual clientInfo when preset is selected
                  if (newClientId) {
                    setPlanClientInfo("");
                  }
                  // Trigger autosave with explicit clientId to ensure it's saved immediately
                  await savePlan(planData, { clientId: newClientId || undefined, websiteId: undefined });
                }}
                onBlur={async () => {
                  // Trigger autosave on blur as well with current state
                  await savePlan(planData);
                }}
                className="w-full rounded-2xl select select-bordered"
                disabled={isLoadingClients}
              >
                <option value="">Nessun preset – inserisci manualmente</option>
                {isLoadingClients ? (
                  <option disabled>Caricamento clienti...</option>
                ) : clients.length === 0 ? (
                  <option disabled>Nessun cliente disponibile. Crea un cliente nelle Impostazioni.</option>
                ) : (
                  clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))
                )}
              </select>
            </div>
            {selectedPlanClientId && (
              <div className="flex flex-col gap-2">
                <label htmlFor="plan-preset-website" className="text-sm font-medium text-left">
                  Sito web (opzionale)
                </label>
                <select
                  id="plan-preset-website"
                  value={selectedPlanWebsiteId}
                  onChange={async (e) => {
                    const newWebsiteId = e.target.value;
                    setSelectedPlanWebsiteId(newWebsiteId);
                    // Clear manual fields when preset is selected
                    if (newWebsiteId) {
                      setPlanWebsite("");
                      setPlanContentType("");
                      setPlanClientInfo("");
                    }
                    // Trigger autosave with explicit websiteId to ensure it's saved immediately
                    await savePlan(planData, { websiteId: newWebsiteId || undefined });
                  }}
                  onBlur={async () => {
                    // Trigger autosave on blur as well
                    await savePlan(planData);
                  }}
                  className="w-full rounded-2xl select select-bordered"
                >
                  <option value="">Nessun preset – inserisci manualmente</option>
                  {websites.map((website) => (
                    <option key={website.id} value={website.id}>
                      {website.website} - {website.contentType}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {(selectedPlanClientId || selectedPlanWebsiteId) && (
              <button
                type="button"
                onClick={() => {
                  setSelectedPlanClientId("");
                  setSelectedPlanWebsiteId("");
                }}
                className="self-start px-4 text-sm rounded-full btn btn-outline btn-sm"
              >
                Rimuovi preset
              </button>
            )}
            {/* Manual fields when no website preset is selected */}
            {!selectedPlanWebsiteId && (
              <>
                <div className="flex flex-col gap-2">
                  <label htmlFor="plan-website" className="text-sm font-medium text-left">
                    Sito web (URL)
                  </label>
                  <input
                    id="plan-website"
                    type="text"
                    value={planWebsite}
                    onChange={(e) => {
                      setPlanWebsite(e.target.value);
                    }}
                    onBlur={async (e) => {
                      const currentValue = e.target.value;
                      if (currentValue.trim()) {
                        const validation = validateUrl(currentValue);
                        if (validation.isValid && validation.normalized) {
                          setPlanWebsite(validation.normalized);
                          // Trigger autosave with normalized value
                          await savePlan(planData, { website: validation.normalized });
                        } else {
                          // Trigger autosave with current value even if invalid (will be validated on save)
                          await savePlan(planData, { website: currentValue });
                        }
                      } else {
                        // Trigger autosave with empty value
                        await savePlan(planData, { website: "" });
                      }
                    }}
                    className="w-full rounded-2xl border border-gray-300 input"
                    placeholder="https://example.com"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label htmlFor="plan-content-type" className="text-sm font-medium text-left">
                    Tipo contenuto
                  </label>
                  <input
                    id="plan-content-type"
                    type="text"
                    value={planContentType}
                    onChange={(e) => {
                      setPlanContentType(e.target.value);
                    }}
                    onInput={(e) => {
                      // Handle browser autocomplete which might not trigger onChange
                      const target = e.target as HTMLInputElement;
                      if (target.value !== planContentType) {
                        setPlanContentType(target.value);
                      }
                    }}
                    onBlur={async (e) => {
                      // Always update state from input value (in case autocomplete didn't trigger onChange)
                      const currentValue = e.target.value;
                      if (currentValue !== planContentType) {
                        setPlanContentType(currentValue);
                      }
                      // Trigger autosave with current value from input
                      await savePlan(planData, { contentType: currentValue });
                    }}
                    className="w-full rounded-2xl border border-gray-300 input"
                    placeholder="es. medico-sanitario"
                  />
                </div>
                {/* Cliente (Info) - only show when no client preset is selected */}
                {!selectedPlanClientId && (
                  <div className="flex flex-col gap-2">
                    <label htmlFor="plan-client-info" className="text-sm font-medium text-left">
                      Cliente (Info)
                    </label>
                    <input
                      id="plan-client-info"
                      type="text"
                      value={planClientInfo}
                      onChange={(e) => {
                        setPlanClientInfo(e.target.value);
                      }}
                      onBlur={async (e) => {
                        // Trigger autosave with current value from input
                        await savePlan(planData, { clientInfo: e.target.value });
                      }}
                      className="w-full rounded-2xl border border-gray-300 input"
                      placeholder="Nome e/o descrizione del cliente"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="divider"></div>
        <div className="flex items-center justify-between mb-4">
          <p className="!text-2xl">Inserisci i dettagli dei contenuti:</p>
          <ImportSpreadsheetButton onClick={handleImportClick} />
        </div>
        <div className="ag-theme-quartz" style={{ height: 400, width: "100%" }}>
          <AgGridReact
            rowData={planData}
            defaultColDef={defaultColDef}
            columnDefs={colDefs}
            editType="fullRow"
            onRowValueChanged={handleRowValueChanged}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            onSelectionChanged={handleSelectionChanged}
          />
        </div>
        <div className="flex self-end gap-4">
          <button onClick={handleAddRow}>Inserisci riga</button>
          <button
            onClick={handleDeleteSelected}
            disabled={selectedRows.length === 0}
          >
            {selectedRows.length > 1
              ? `Elimina ${selectedRows.length} righe`
              : "Elimina riga"}
          </button>
        </div>
        <button
          onClick={handleGeneratePlan}
          disabled={isGenerating || isImporting}
          className="self-center px-6 my-1 text-base font-medium border-none rounded-full outline-none text-primary-content bg-accent btn flex-nowrap whitespace-nowrap w-fit"
        >
          <ArticleOutlinedIcon className="w-4" />
          Genera piano editoriale
        </button>
      </section>
      <ImportSpreadsheetModal
        isOpen={isImportModalOpen}
        onClose={handleImportClose}
        onImportConfirm={handleImportConfirm}
        columnDefs={colDefs}
      />
    </section>
  );
};

export default GeneratePlan;
